import { useCallback, useMemo, useState } from 'react';
import {
  EXERCISE_ORDER_FIXED,
  EXERCISE_ORDER_RANDOM,
  type ExerciseOrder,
} from '@cavaquinho/shared';
import { ApiError, api } from '../../lib/api/client.js';
import type { AdminLesson } from '../../lib/api/types.js';
import { Button } from '../../ui/primitives/Button.tsx';
import { Field } from '../../ui/primitives/Field.tsx';
import { IconButton } from '../../ui/primitives/IconButton.tsx';
import { Input } from '../../ui/primitives/Input.tsx';
import { Select } from '../../ui/primitives/Select.tsx';
import { Text } from '../../ui/primitives/Text.tsx';
import { DataTable, type DataTableColumn } from '../../ui/components/DataTable/DataTable.tsx';
import { Drawer, DrawerSection } from '../../ui/components/Drawer/Drawer.tsx';
import {
  ExerciseConfigForm,
  authorableTypes,
  formFor,
  labelForType,
  validateConfigFor,
} from './ExerciseConfigForm.tsx';
import { ConfirmDelete } from './ConfirmDelete.tsx';
import { describeExerciseDeletion, type FieldViolation } from './validation.js';
import styles from './admin.module.css';

interface LessonEditorProps {
  /** Absent when authoring a new Lesson. */
  lesson?: AdminLesson;
  onCancel: () => void;
  onSaved: () => void;
}

/** An Exercise being edited: a saved one carries its id, a new one does not. */
interface DraftExercise {
  id?: string;
  exerciseType: string;
  /** Internal name, admin-only. Blank is sent as untitled. */
  title: string;
  config: unknown;
  /**
   * The configuration last edited under each OTHER type during this editing
   * session, so switching the type away and back restores what was there
   * rather than starting over.
   */
  configsByType: Record<string, unknown>;
  /** A stable React key for a draft that has no id yet. */
  key: string;
}

/**
 * The Lesson editor (T099; FR-001, FR-040, FR-048).
 *
 * A NEW LESSON IS CREATED WHOLE — title and every Exercise in one request —
 * because FR-048 says a Lesson is never empty. Creating the Lesson first and
 * adding Exercises afterwards would publish an empty one in between, and leave
 * one stranded if the second step failed. An EXISTING Lesson is edited
 * incrementally, which is safe for the same reason in reverse: it already has
 * at least one Exercise, and the server refuses to let the last one go.
 *
 * Validation is mirrored inline for immediacy and is never the authority: the
 * server validates every write and its `422` field list is merged into the
 * same `violations` the mirror produces, so a rule only the server knows
 * renders in exactly the same place as one the mirror predicted.
 *
 * The Exercises are a § 14 Data table for the same reason the Lesson list is —
 * they are a set compared across, in an order that matters — and configuring
 * one happens in the Drawer beside it (§ 16), so the table stays the dense
 * surface and the form gets its own focus column.
 *
 * NOTHING IS SENT UNTIL EVERY EXERCISE WOULD BE ACCEPTED, and every created
 * Exercise's id is recorded the instant it comes back. Both halves matter, and
 * the reason is a bug this editor used to have: the save loop sent one request
 * per Exercise, discarded the ids it got back, and left the drafts marked
 * "Novo" — so a rejection on the fourth Exercise had already committed the
 * first three, and pressing Salvar again created them a SECOND time. Repeat
 * that while hunting for the field at fault and a Lesson fills with
 * duplicates. The mirror check below is therefore not merely an immediacy
 * nicety any more: it is what keeps a partial write from happening at all,
 * and `rememberCreated` is what makes the retry after one idempotent anyway.
 *
 * A REJECTION NAMES THE EXERCISE IT CAME FROM. The server's 422 carries field
 * paths (`config.audioUrl`) and no Exercise index, so the loop remembers which
 * entry it was sending and pins the violations to that one — `violationsByKey`
 * rather than one flat list. Showing them against whichever Exercise happened
 * to be selected sent administrators to correct the wrong one, which is how
 * one mistake turned into several rounds of saving.
 *
 * ORDER IS PART OF THE SAVE, NOT A REQUEST OF ITS OWN. Moving an Exercise
 * rearranges the array and nothing else; the full ordered id list goes up once
 * at the end of `save`, when every entry has an id. That is what lets an
 * unsaved Exercise be moved at all — the old code filtered draft ids out of
 * the payload and the server rightly rejected the short list as not the
 * current set — and it is also why a new Exercise now lands where it was put
 * rather than always at the end.
 */
export function LessonEditor({ lesson, onCancel, onSaved }: LessonEditorProps) {
  const isNew = lesson === undefined;
  const [title, setTitle] = useState(lesson?.title ?? '');
  const [exercises, setExercises] = useState<DraftExercise[]>(
    (lesson?.exercises ?? []).map((exercise, index) => ({
      id: exercise.id,
      exerciseType: exercise.exerciseType,
      title: exercise.title ?? '',
      config: exercise.config,
      configsByType: {},
      key: `${exercise.id}-${index}`,
    })),
  );
  const [exerciseOrder, setExerciseOrder] = useState<ExerciseOrder>(
    lesson?.exerciseOrder ?? EXERCISE_ORDER_FIXED,
  );
  /**
   * Violations per Exercise, keyed by `DraftExercise.key` rather than by index
   * — an index would point at the wrong Exercise the moment one is moved or
   * removed, which is exactly the misattribution this replaced.
   */
  const [violationsByKey, setViolationsByKey] = useState<Record<string, FieldViolation[]>>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<DraftExercise | null>(null);
  const [selected, setSelected] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const current = exercises[selected];
  const currentViolations = current ? violationsByKey[current.key] ?? EMPTY_VIOLATIONS : EMPTY_VIOLATIONS;
  // A `random` Lesson still HAS an authored order — it is simply not the one
  // learners meet, so the move controls are stood down rather than hidden and
  // the reason is said out loud.
  const randomised = exerciseOrder === EXERCISE_ORDER_RANDOM;

  /** Clears one Exercise's violations, leaving every other Exercise's intact. */
  const clearViolations = useCallback((key: string) => {
    setViolationsByKey((previous) => {
      if (!previous[key]) return previous;
      const next = { ...previous };
      delete next[key];
      return next;
    });
  }, []);

  const openDrawer = useCallback((index: number) => {
    setSelected(index);
    setDrawerOpen(true);
  }, []);

  const updateCurrent = useCallback(
    (config: unknown) => {
      if (!current) return;
      setExercises((previous) =>
        previous.map((entry, index) => (index === selected ? { ...entry, config } : entry)),
      );
      // Re-mirrored as the field is edited, so a violation the server reported
      // disappears the moment the configuration would satisfy it rather than
      // lingering until the next save.
      const next = validateConfigFor(current.exerciseType, config);
      setViolationsByKey((previous) => {
        if (next.length === 0) {
          if (!previous[current.key]) return previous;
          const without = { ...previous };
          delete without[current.key];
          return without;
        }
        return { ...previous, [current.key]: next };
      });
    },
    [selected, current],
  );

  const renameCurrent = useCallback(
    (title: string) => {
      setExercises((previous) =>
        previous.map((entry, index) => (index === selected ? { ...entry, title } : entry)),
      );
    },
    [selected],
  );

  /**
   * Changes an Exercise's type, saved or not — a saved one is updated in
   * place on save, never recreated. The config it carried for the old type
   * means nothing to the new one, so it is set aside rather than merged: the
   * new type starts from what it last held in this session, or from its own
   * `empty()`, and switching back restores the old config untouched. Nothing
   * reaches the server until Salvar.
   */
  const changeType = useCallback(
    (exerciseType: string) => {
      const form = formFor(exerciseType);
      if (!form) return;
      setExercises((previous) =>
        previous.map((entry, index) => {
          if (index !== selected || entry.exerciseType === exerciseType) return entry;
          const configsByType = { ...entry.configsByType, [entry.exerciseType]: entry.config };
          return {
            ...entry,
            exerciseType,
            config: configsByType[exerciseType] ?? form.empty(),
            configsByType,
          };
        }),
      );
      if (current) clearViolations(current.key);
    },
    [selected, current, clearViolations],
  );

  // Note identification is the first Exercise Type this platform shipped,
  // not the only one — it is only this default, not a ceiling on what an
  // administrator can add (multiple_choice, and whatever the registry gains
  // next, all appear here for free, via the type Select inside the Drawer).
  const addExercise = useCallback(() => {
    const exerciseType = authorableTypes()[0];
    const form = exerciseType ? formFor(exerciseType) : null;
    if (!exerciseType || !form) return;
    const config = form.empty();
    const key = `draft-${exercises.length}-${Date.now()}`;
    setExercises((previous) => [
      ...previous,
      { exerciseType, title: '', config, configsByType: {}, key },
    ]);
    // Marked incomplete AS IT IS ADDED. `empty()` is a blank the server will
    // refuse, and the row saying so from the start — with Salvar held back —
    // is the honest reading of what has just been created. Leaving it looking
    // ready until Salvar was pressed is what made the old partial write feel
    // like the screen's fault rather than an unfinished exercise.
    const initial = validateConfigFor(exerciseType, config);
    if (initial.length > 0) setViolationsByKey((previous) => ({ ...previous, [key]: initial }));
    openDrawer(exercises.length);
  }, [exercises.length, openDrawer]);

  /**
   * Moves an Exercise within the Lesson. LOCAL ONLY — the array is the order,
   * and `save` persists it once every entry has an id.
   *
   * It used to send the order immediately, which made it unusable in the one
   * situation it was most needed: the payload is the full ordered id list, a
   * draft has no id to put in it, and the server correctly refused the short
   * list as not the current set. So adding an Exercise silently disabled
   * reordering until the Lesson had been saved, with only "Não foi possível
   * reordenar os exercícios." to explain it. Deferring also matches what this
   * screen already promises for a saved Lesson — "As alterações são salvas ao
   * confirmar."
   */
  const move = useCallback(
    (index: number, delta: number) => {
      const target = index + delta;
      if (target < 0 || target >= exercises.length || busy) return;

      const next = [...exercises];
      const [moved] = next.splice(index, 1);
      if (!moved) return;
      next.splice(target, 0, moved);

      setExercises(next);
      setSelected(target);
    },
    [exercises, busy],
  );

  /** Records a created Exercise's id against its draft, so a retry PATCHes it. */
  const rememberCreated = useCallback((key: string, id: string) => {
    setExercises((previous) => previous.map((entry) => (entry.key === key ? { ...entry, id } : entry)));
  }, []);

  const save = useCallback(async () => {
    setServerError(null);

    // THE MIRROR RUNS OVER EVERY EXERCISE FIRST, not just the selected one, and
    // nothing is sent if any of them would be refused. A new Exercise starts
    // from its type's `empty()` — a blank audioUrl, a blank question — which
    // the server rejects by design, so "add four, fill three, save" used to be
    // a guaranteed partial write. This is the gate that makes it a no-op.
    const found: Record<string, FieldViolation[]> = {};
    for (const entry of exercises) {
      const entryViolations = validateConfigFor(entry.exerciseType, entry.config);
      if (entryViolations.length > 0) found[entry.key] = entryViolations;
    }
    if (Object.keys(found).length > 0) {
      const firstBad = exercises.findIndex((entry) => found[entry.key]);
      setViolationsByKey(found);
      // Opened ON the offending Exercise: the violations are field-level and
      // belong under their own controls, and an administrator should not have
      // to hunt the table for which row the banner means.
      setSelected(firstBad);
      setDrawerOpen(true);
      setServerError(
        Object.keys(found).length === 1
          ? `Complete o exercício ${firstBad + 1} antes de salvar.`
          : `${Object.keys(found).length} exercícios estão incompletos. Comece pelo ${firstBad + 1}.`,
      );
      return;
    }
    setViolationsByKey({});

    setBusy(true);
    // Which Exercise the request in flight belongs to, so a 422 carrying only
    // field paths can still be pinned to the right row.
    let attributeTo: string | null = null;
    try {
      if (isNew) {
        await api.createLesson({
          title,
          exerciseOrder,
          exercises: exercises.map((entry) => ({
            exerciseType: entry.exerciseType,
            title: titleOrNull(entry.title),
            config: entry.config,
          })),
        });
      } else if (lesson) {
        // One patch for the Lesson's own fields, and only when something
        // changed — an empty patch is a 422 by design.
        const patch = {
          ...(title !== lesson.title ? { title } : {}),
          ...(exerciseOrder !== lesson.exerciseOrder ? { exerciseOrder } : {}),
        };
        if (Object.keys(patch).length > 0) await api.updateLesson(lesson.id, patch);

        // The ids in the order the administrator arranged them, collected as
        // we go: a saved Exercise contributes its own, a new one the id the
        // server just minted.
        const ordered: string[] = [];
        for (const entry of exercises) {
          attributeTo = entry.key;
          const exerciseTitle = titleOrNull(entry.title);
          if (entry.id) {
            await api.updateExercise(entry.id, entry.exerciseType, exerciseTitle, entry.config);
            ordered.push(entry.id);
          } else {
            const created = await api.createExercise(
              lesson.id,
              entry.exerciseType,
              exerciseTitle,
              entry.config,
            );
            // Recorded BEFORE the next request, so a failure on the one after
            // leaves this draft holding its id and the retry updates it
            // instead of creating a second copy.
            rememberCreated(entry.key, created.id);
            ordered.push(created.id);
          }
        }

        // The order last, when every entry has an id. A create appends at the
        // end of the Lesson, so this is also what moves a newly added Exercise
        // to the place it was put. Skipped when the sequence already matches
        // what the server holds.
        attributeTo = null;
        if (!sameSequence(ordered, lesson.exercises.map((entry) => entry.id))) {
          await api.reorderExercises(lesson.id, ordered);
        }
      }
      onSaved();
    } catch (error) {
      const fields = error instanceof ApiError ? error.envelope?.fields : undefined;
      if (fields?.length && attributeTo) {
        // A rule only the server knows, pinned to the Exercise it came from so
        // it renders beneath the very field it names.
        const key = attributeTo;
        setViolationsByKey({ [key]: fields as FieldViolation[] });
        setSelected(exercises.findIndex((entry) => entry.key === key));
        setDrawerOpen(true);
        setServerError('Corrija os campos destacados.');
      } else if (fields?.length) {
        // Nothing to pin it to — the whole-Lesson create, or the reorder. The
        // messages go in the banner rather than under an arbitrary field,
        // because a violation shown in the wrong place is worse than one shown
        // plainly.
        setServerError(fields.map((violation) => violation.message).join(' '));
      } else {
        setServerError('Não foi possível salvar.');
      }
    } finally {
      setBusy(false);
    }
  }, [isNew, lesson, title, exerciseOrder, exercises, onSaved, rememberCreated]);

  const confirmDeleteExercise = useCallback(async () => {
    if (!pendingDelete) return;
    // An unsaved draft is simply dropped; there is nothing on the server.
    if (!pendingDelete.id) {
      setExercises((previous) => previous.filter((entry) => entry.key !== pendingDelete.key));
      // Its violations go with it. A removed Exercise whose violations stayed
      // behind would keep Salvar disabled over a row that is no longer on
      // screen — the worst kind of block, one with nothing to correct.
      clearViolations(pendingDelete.key);
      setSelected(0);
      setPendingDelete(null);
      return;
    }

    setBusy(true);
    try {
      await api.deleteExercise(pendingDelete.id);
      setExercises((previous) => previous.filter((entry) => entry.id !== pendingDelete.id));
      clearViolations(pendingDelete.key);
      setSelected(0);
      setPendingDelete(null);
    } catch (error) {
      // FR-048 — the server refuses to leave a Lesson empty, and says so in
      // the administrator's own terms rather than as a generic failure.
      setServerError(
        error instanceof ApiError && error.code === 'validation_failed'
          ? 'Uma lição precisa manter ao menos um exercício. Remova a lição inteira, ou adicione outro exercício antes.'
          : 'Não foi possível remover o exercício.',
      );
      setPendingDelete(null);
    } finally {
      setBusy(false);
    }
  }, [pendingDelete, clearViolations]);

  const titleInvalid = title.trim() === '';
  const incomplete = exercises.filter((entry) => violationsByKey[entry.key]).length;
  /**
   * Salvar is held back while anything is known to be wrong. It was not
   * before: an Exercise could be sent in a state the server was certain to
   * refuse, and the refusal came AFTER earlier Exercises had already been
   * created. `save` re-checks every Exercise itself, so this is the affordance
   * saying so rather than the enforcement.
   */
  const canSave = !busy && !titleInvalid && exercises.length > 0 && incomplete === 0;

  const columns: DataTableColumn<{ entry: DraftExercise; index: number }>[] = useMemo(
    () => [
      {
        id: 'order',
        header: '#',
        width: 'min',
        align: 'end',
        cell: ({ index }) => <span className="type-numeric-inline">{index + 1}</span>,
      },
      {
        id: 'title',
        header: 'Título',
        rowHeader: true,
        cell: ({ entry }) =>
          entry.title.trim() ? (
            entry.title
          ) : (
            <Text as="span" variant="body-sm" tone="muted">
              Sem título
            </Text>
          ),
      },
      {
        id: 'type',
        header: 'Tipo',
        cell: ({ entry }) => labelForType(entry.exerciseType),
      },
      {
        id: 'state',
        header: 'Estado',
        width: 'min',
        /*
          An incomplete Exercise says so HERE, in the row, and not only inside
          the Drawer it has to be opened to see. The table is the surface an
          administrator scans before pressing Salvar, so what is blocking the
          save has to be visible from it.
        */
        cell: ({ entry }) =>
          violationsByKey[entry.key] ? (
            <Text as="span" variant="body-sm" tone="error">
              Incompleto
            </Text>
          ) : (
            <Text as="span" variant="body-sm" tone="muted">
              {entry.id ? 'Salvo' : 'Novo'}
            </Text>
          ),
      },
      {
        id: 'actions',
        header: 'Ações',
        width: 'min',
        align: 'end',
        cell: ({ entry, index }) => (
          <div className={styles.rowActions}>
            <span data-testid="select-exercise">
              <Button variant="ghost" size="compact" onClick={() => openDrawer(index)}>
                Configurar
              </Button>
            </span>
            <span data-testid="exercise-move-up">
              <IconButton
                icon="arrow-up"
                size="compact"
                shape="square"
                label={`Mover exercício ${index + 1} para cima`}
                disabled={index === 0 || busy || randomised}
                disabledReason={
                  randomised
                    ? 'A ordem é sorteada para cada aluno.'
                    : index === 0
                      ? 'Já é o primeiro.'
                      : 'Salvando.'
                }
                onClick={() => move(index, -1)}
              />
            </span>
            <span data-testid="exercise-move-down">
              <IconButton
                icon="arrow-down"
                size="compact"
                shape="square"
                label={`Mover exercício ${index + 1} para baixo`}
                disabled={index === exercises.length - 1 || busy || randomised}
                disabledReason={
                  randomised
                    ? 'A ordem é sorteada para cada aluno.'
                    : index === exercises.length - 1
                      ? 'Já é o último.'
                      : 'Salvando.'
                }
                onClick={() => move(index, 1)}
              />
            </span>
            <span data-testid="delete-exercise">
              <IconButton
                icon="trash"
                size="compact"
                shape="square"
                label={`Remover exercício ${index + 1}`}
                onClick={() => setPendingDelete(entry)}
              />
            </span>
          </div>
        ),
      },
    ],
    [busy, exercises.length, move, openDrawer, randomised, violationsByKey],
  );

  return (
    <div className={styles.page} data-testid="lesson-editor">
      <header className={styles.header}>
        <div className={styles.heading}>
          <Text as="h1" variant="subheading">
            {isNew ? 'Nova lição' : 'Editar lição'}
          </Text>
          <Text as="p" variant="body-sm" tone="muted">
            {isNew
              ? 'A lição e seus exercícios são criados juntos.'
              : 'As alterações são salvas ao confirmar.'}
          </Text>
        </div>
      </header>

      {serverError ? (
        <div role="alert" data-testid="editor-error">
          <Text as="p" variant="body-sm" tone="error">
            {serverError}
          </Text>
        </div>
      ) : null}

      <div className={styles.formColumn}>
        <Field
          label="Título"
          htmlFor="lesson-title"
          error={titleInvalid ? 'Dê um título à lição.' : undefined}
        >
          <Input id="lesson-title" value={title} invalid={titleInvalid} onChange={setTitle} />
        </Field>

        {/*
          The choice between an arranged order and a sorted one. Switching to
          "Sorteada" changes nothing about the Exercises below: the arrangement
          stays exactly as authored and simply stops being the one learners
          meet, so switching back restores it whole. Each learner's draw is
          their own AND is the same on every visit — nobody is moved around
          mid-lição by reloading the page.
        */}
        <Field
          label="Ordem dos exercícios"
          htmlFor="lesson-exercise-order"
          hint={
            randomised
              ? 'Cada aluno recebe uma ordem sorteada, sempre a mesma para ele. A ordem abaixo fica guardada.'
              : 'Todos os alunos seguem a ordem abaixo.'
          }
        >
          <Select
            id="lesson-exercise-order"
            value={exerciseOrder}
            options={[
              { value: EXERCISE_ORDER_FIXED, label: 'Fixa' },
              { value: EXERCISE_ORDER_RANDOM, label: 'Sorteada por aluno' },
            ]}
            onChange={(value) => setExerciseOrder(value as ExerciseOrder)}
          />
        </Field>
      </div>

      <section className={styles.section}>
        <div className={styles.header}>
          <Text as="h2" variant="subheading">
            Exercícios
          </Text>
          <span data-testid="add-exercise">
            <Button variant="secondary" size="compact" icon="plus" onClick={addExercise}>
              Adicionar exercício
            </Button>
          </span>
        </div>

        {/*
          Said once, above the table, rather than left for whoever hovers a
          disabled move button to discover: under a sorted order the № column
          is still a real position — it is what this Lesson goes back to if the
          setting is switched off — but it is not the position any learner
          meets, and a table of numbered rows would otherwise imply it is.
        */}
        {randomised ? (
          <Text as="p" variant="body-sm" tone="muted">
            A ordem abaixo fica guardada, mas cada aluno recebe a sua: por isso os exercícios não
            podem ser movidos enquanto a ordem estiver sorteada.
          </Text>
        ) : null}

        <DataTable
          caption="Exercícios desta lição"
          testId="exercise-rows"
          rowTestId="exercise-row"
          columns={columns}
          rows={exercises.map((entry, index) => ({ entry, index }))}
          rowKey={({ entry }) => entry.key}
          busy={busy}
          empty={
            // FR-048, said before the server has to say it.
            <Text as="p" variant="body-sm" tone="muted">
              Uma lição precisa de ao menos um exercício.
            </Text>
          }
        />
      </section>

      <footer className={styles.footer}>
        <span data-testid="cancel-lesson">
          <Button variant="ghost" onClick={onCancel}>
            Cancelar
          </Button>
        </span>
        <span data-testid="save-lesson">
          {canSave ? (
            <Button id="save-lesson" onClick={() => void save()}>
              Salvar
            </Button>
          ) : (
            <Button
              id="save-lesson"
              disabled
              disabledReason={
                busy
                  ? 'Salvando.'
                  : titleInvalid
                    ? 'Dê um título à lição.'
                    : exercises.length === 0
                      ? 'Adicione ao menos um exercício.'
                      : incomplete === 1
                        ? 'Um exercício está incompleto.'
                        : `${incomplete} exercícios estão incompletos.`
              }
            >
              Salvar
            </Button>
          )}
        </span>
      </footer>

      {pendingDelete ? (
        <ConfirmDelete
          title="Remover este exercício?"
          impact={describeExerciseDeletion()}
          confirmLabel="Remover exercício"
          busy={busy}
          onCancel={() => setPendingDelete(null)}
          onConfirm={() => void confirmDeleteExercise()}
        />
      ) : null}

      {current ? (
        <Drawer
          open={drawerOpen}
          title={current.title.trim() || `Exercício ${selected + 1}`}
          description={labelForType(current.exerciseType)}
          onClose={() => setDrawerOpen(false)}
          footer={
            <Button id="close-exercise-drawer" onClick={() => setDrawerOpen(false)}>
              Concluído
            </Button>
          }
        >
          <DrawerSection
            title="Exercício"
            description={
              current.id
                ? 'Trocar o tipo mantém o exercício no mesmo lugar da lição; a configuração passa a ser a do novo tipo.'
                : 'O tipo pode ser trocado a qualquer momento.'
            }
            testId="exercise-identity"
          >
            <Field
              size="compact"
              label="Título interno"
              htmlFor="exercise-title"
              hint="Só aparece no painel de administração, para identificar o exercício."
            >
              <Input
                id="exercise-title"
                value={current.title}
                describedBy="exercise-title-hint"
                onChange={renameCurrent}
              />
            </Field>
            <Field size="compact" label="Tipo de exercício" htmlFor="exercise-type">
              <Select
                id="exercise-type"
                value={current.exerciseType}
                options={authorableTypes().map((type) => ({
                  value: type,
                  label: labelForType(type),
                }))}
                onChange={changeType}
              />
            </Field>
          </DrawerSection>

          <ExerciseConfigForm
            exerciseType={current.exerciseType}
            config={current.config}
            violations={currentViolations}
            onChange={updateCurrent}
          />
        </Drawer>
      ) : null}
    </div>
  );
}

/** Blank means untitled; the server stores that as null. */
function titleOrNull(title: string): string | null {
  const trimmed = title.trim();
  return trimmed === '' ? null : trimmed;
}

/**
 * Whether two id sequences are the same list in the same order.
 *
 * `save` uses it to decide whether the order needs sending at all: a Lesson
 * whose Exercises were only edited should not also renumber its scope.
 */
function sameSequence(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((id, index) => id === b[index]);
}

/**
 * One frozen empty array for every Exercise with nothing wrong with it — a
 * fresh `[]` per render would be a new prop identity each time and re-render
 * the whole configuration form for no reason.
 */
const EMPTY_VIOLATIONS: readonly FieldViolation[] = Object.freeze([]);
